import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Table, Tabs, Spin, Tag } from "antd";

import css from "./CycleAvailableDishesComponent.module.css";
import { cycleAvailableDishesActions } from "../../../store/slices/menu-slices";
import { getCurrentCycleDay } from "../../../helpers";
import { ButtonEdit, ImageReusable } from "../../../ui";
import SelectCycleAvailableDishesModal from "../menuSectionModals/SelectCycleAvailableDishesModal/SelectCycleAvailableDishesModal";
import { CloneAvailableDishesToCycleComponent } from "../CloneAvailableDishesToCycleComponent/CloneAvailableDishesToCycleComponent";

const CycleAvailableDishesComponent = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const { cycleConfig } = useSelector((store) => store.appSettings);
    const { dishesArray, status, trigger } = useSelector((store) => store.cycleAvailableDishes);
    const lengthDays = cycleConfig?.lengthDays || 7;

    // On entering the screen, open directly on today's cycle day (Day 1 if the
    // cycle window isn't configured yet).
    const currentCycleDay = getCurrentCycleDay(cycleConfig);
    const [activeDay, setActiveDay] = useState(() => currentCycleDay || 1);
    const [isOpen, setIsOpen] = useState(false);

    const refresh = useCallback(() => {
        dispatch(cycleAvailableDishesActions.getAllCycleAvailableDishes(activeDay));
    }, [dispatch, activeDay]);

    useEffect(() => {
        dispatch(cycleAvailableDishesActions.getAllCycleAvailableDishes(activeDay));
    }, [activeDay, trigger, dispatch]);

    const handleTabChange = (key) => {
        setActiveDay(Number(key));
    };

    const columns = useMemo(() => [
        {
            title: t("Image"),
            dataIndex: ["dish", "img"],
            key: "img",
            width: "10%",
            render: (img) => (
                <ImageReusable src={img} alt="" className={css.image} />
            ),
        },
        {
            title: t("modal-name-input-new-category"),
            dataIndex: ["dish", "name"],
            key: "name",
            width: "30%",
            render: (name, record) => {
                const displayName = name?.[currentLanguage] || name?.["en"] || "";
                const { energy, proteins, fats, carbs } = record.dish;
                return (
                    <div>
                        <div className={css.dishName}>{displayName}</div>
                        <div className={css.nutrition}>
                            <span>{energy || 0} kcal</span>
                            <span>P: {proteins || 0}g</span>
                            <span>F: {fats || 0}g</span>
                            <span>C: {carbs || 0}g</span>
                        </div>
                    </div>
                );
            },
        },
        {
            title: t("Weight"),
            dataIndex: ["dish", "weight"],
            key: "weight",
            width: "10%",
            align: "center",
            render: (v) => v || 0,
        },
        {
            title: t("weight-netto"),
            dataIndex: ["dish", "weightNetto"],
            key: "weightNetto",
            width: "10%",
            align: "center",
            render: (v) => v || 0,
        },
        {
            title: t("modal-price"),
            dataIndex: ["dish", "price"],
            key: "price",
            width: "10%",
            align: "center",
            render: (v) => v || 0,
        },
        {
            title: t("Category"),
            dataIndex: ["dish", "categories"],
            key: "category",
            width: "20%",
            render: (categories) =>
                categories
                    ?.map((c) => c.name?.[currentLanguage] || c.name?.["en"])
                    .join(", ") || t("unknown-category"),
        },
    ], [t, currentLanguage]);

    const tabItems = Array.from({ length: lengthDays }, (_, i) => {
        const day = i + 1;
        return {
            key: String(day),
            label: (
                <span>
                    {t("Day")} {day}
                    {day === currentCycleDay && (
                        <Tag color="green" style={{ marginInlineStart: 6 }}>
                            {t("Today")}
                        </Tag>
                    )}
                </span>
            ),
            children: (
                <div className={css.tableWrap}>
                    <Table
                        columns={columns}
                        dataSource={dishesArray}
                        rowKey={(r) => r.id}
                        pagination={false}
                        loading={status === "loading"}
                        locale={{ emptyText: t("No dishes set for this day") }}
                    />
                </div>
            ),
        };
    });

    return (
        <div className={css.wrap}>
            <div className={css.toolbar}>
                <ButtonEdit onClick={() => setIsOpen(true)} />
                <CloneAvailableDishesToCycleComponent activeDay={activeDay} onSuccess={refresh} />
                {currentCycleDay && (
                    <Tag color="green" style={{ marginInlineStart: "auto" }}>
                        {t("Today")}: {t("Day")} {currentCycleDay}
                    </Tag>
                )}
            </div>

            <Tabs
                activeKey={String(activeDay)}
                onChange={handleTabChange}
                items={tabItems}
            />

            {isOpen && (
                <SelectCycleAvailableDishesModal
                    cycleDay={activeDay}
                    setIsOpen={setIsOpen}
                />
            )}
        </div>
    );
};

export { CycleAvailableDishesComponent };
